import {
  Button,
  ComposedModal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextArea,
  TextInput,
} from '@carbon/react';
import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import type { FailedApiResponse } from 'components/well-page-content/types';
import ErrorToast from 'components/error-toast';
import request from 'lib/request';
import { createWorkspaceLoadableAtom } from './atoms';
import type { CreateWorkspaceResponse, WorkspacesQueryResponse } from './types';
import { nextAvailableWorkspaceName } from './utils';
import style from './create-workspace-modal.module.scss';

interface CreateWorkspaceModalProps {
  entityId: number;
  entityName: string;
  open: boolean;
  onClose: () => void;
  // Creation now runs as a background workflow, so instead of navigating to the
  // (still-building) workspace we hand the kickoff response up to the entity
  // page, which shows a live progress chip until it's ready.
  onCreated: (result: CreateWorkspaceResponse) => void;
}

const toYYYYMMDD = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}${m}${day}`;
};

export const CreateWorkspaceModal = ({
  entityId,
  entityName,
  open,
  onClose,
  onCreated,
}: CreateWorkspaceModalProps) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);
  // When the auto-suggested name was bumped past a collision, the suffixed
  // name we landed on — surfaced as helper text so the user sees why it
  // differs from the plain base. Empty when the base name was free.
  const [adjustedName, setAdjustedName] = useState('');
  const [loadable, createWorkspace] = useAtom(createWorkspaceLoadableAtom);
  const submittingRef = useRef(false);
  // The base default the open effect set; the auto-suggest only overrides the
  // field while it still holds this value (i.e. the user hasn't edited it).
  const baseNameRef = useRef('');

  useEffect(() => {
    if (!open) return;

    const base = `${entityName} ${toYYYYMMDD(new Date())}`;
    // Show the base instantly (no flicker, submit stays enabled), then refine
    // to the next free name once the lookup returns.
    baseNameRef.current = base;
    setName(base);
    setDescription('');
    setHasSubmitted(false);
    setAdjustedName('');

    const abortController = new AbortController();

    (async () => {
      const response = await request.post<
        WorkspacesQueryResponse,
        FailedApiResponse
      >(
        '/queries/interest_certification_workspaces',
        {
          query: {
            entity_id: entityId,
            search: base,
            page: 1,
            page_size: 100,
          },
        },
        abortController
      );

      if (response.type !== 'success' || !response.data) return;

      const available = nextAvailableWorkspaceName(
        base,
        response.data.results.map((workspace) => workspace.name)
      );
      if (available === base) return;

      // Only override if the user hasn't edited the field meanwhile — the
      // field must still hold the base default. (The abort cleanup already
      // prevents a stale response from a prior open landing here.)
      if (baseNameRef.current !== base) return;
      setName(available);
      setAdjustedName(available);
    })();

    // Abort the in-flight lookup if the modal closes or reopens, preventing
    // a stale response from clobbering a fresh open (race between opens) or
    // setting state after close.
    return () => abortController.abort();
  }, [open, entityId, entityName]);

  const loading = loadable.state === 'loading';
  const canSubmit = name.trim().length > 0 && !loading;

  const handleClose = () => {
    if (!loading) onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit || submittingRef.current) return;
    submittingRef.current = true;
    setHasSubmitted(true);
    try {
      const result = await createWorkspace(
        entityId,
        name.trim(),
        description.trim()
      );
      onClose();
      onCreated(result);
    } catch {
      // Error reflected in loadable.state; ErrorToast renders below.
    } finally {
      submittingRef.current = false;
    }
  };

  const handleNameKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') handleSubmit();
  };

  const errorMessage =
    loadable.state === 'hasError'
      ? loadable.error instanceof Error
        ? loadable.error.message
        : 'Failed to create workspace.'
      : undefined;

  return (
    <>
      <ComposedModal
        open={open}
        onClose={handleClose}
        preventCloseOnClickOutside
        size="sm"
      >
        <ModalHeader
          label="Interest Confirmation"
          title="Create Certify Interest Workspace"
        />
        <ModalBody>
          <p className={style.intro}>
            Inside the Workspace you can certify slot interests per tract and
            promote the elected values for each slot. Past Workspaces stay on
            the Entity page as a record of completed work.
          </p>
          <TextInput
            id="create-workspace-name"
            labelText="Workspace name"
            value={name}
            disabled={loading}
            helperText={
              adjustedName
                ? `Name in use — using "${adjustedName}".`
                : undefined
            }
            // Suppress the browser's autofill dropdown of previously
            // submitted names — it covers the modal and adds little value.
            autoComplete="off"
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setName(e.target.value);
              // Once the user edits the field, drop the auto-adjust notice and
              // clear the base marker so a late lookup can't override the edit.
              setAdjustedName('');
              baseNameRef.current = '';
            }}
            onKeyDown={handleNameKeyDown}
            data-modal-primary-focus
          />
          <TextArea
            id="create-workspace-description"
            className={style.description}
            labelText="Description (optional)"
            value={description}
            disabled={loading}
            rows={3}
            placeholder="Add a note about this workspace…"
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
              setDescription(e.target.value)
            }
          />
        </ModalBody>
        <ModalFooter>
          <Button kind="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button kind="primary" onClick={handleSubmit} disabled={!canSubmit}>
            {loading ? 'Creating…' : 'Create Workspace'}
          </Button>
        </ModalFooter>
      </ComposedModal>
      {hasSubmitted && errorMessage && (
        <ErrorToast key={errorMessage} message={errorMessage} />
      )}
    </>
  );
};
